<template>
  <div class="container">
    <div v-if="loading" class="loader">
      <div class="lds-ellipsis">
        <div></div>
        <div></div>
        <div></div>
        <div></div>
      </div>
    </div>
    <template v-else>
      <div class="page-header">
        <h1>Foods Management</h1>
        <button @click="refreshFoods" class="refresh-btn" title="Refresh foods data">
          <img src="@/assets/refresh.svg" alt="Refresh" />
        </button>
      </div>
      <AdminMenuView
        :visible="showMenuView"
        :menu-id="selectedMenuId"
        :food-id="selectedFoodId"
        @close="closeMenuView"
      />
      <div>
      <div class="controls-container">
        <div class="search-container">
          <p class="admin-label">Search</p>
          <input
            type="text"
            v-model="searchQuery"
            @keyup="handleSearch"
            placeholder="Search foods..."
            class="search-input"
          />
        </div>
        <button @click="resetColumnsToDefault" class="reset-columns-btn" title="Reset columns to default">
          Reset Columns
        </button>
      </div>
      <div class="menu-id-container">
        <p class="admin-label">Menu IDs</p>
        <div class="menu-id-inputs">
          <div 
            v-for="(menuId, index) in menuIds" 
            :key="index" 
            class="menu-id-input-wrapper"
          >
            <input
              type="text"
              v-model="menuIds[index]"
              @keyup="handleMenuIdSearch"
              :placeholder="index === 0 ? 'Menu ID...' : 'Menu ID...'"
              class="menu-id-input"
            />
            <button 
              v-if="menuIds.length > 1"
              @click="removeMenuId(index)"
              class="remove-menu-id-btn"
              title="Remove menu ID"
            >
              ×
            </button>
          </div>
          <button @click="addMenuId" class="add-menu-id-btn" title="Add another menu ID">
            +
          </button>
        </div>
      </div>
      <div v-if="error" class="error-message">
        <p>Error loading foods: {{ error }}</p>
      </div>
      <div v-else-if="foodsData && foodsArray.length > 0" class="spreadsheet-wrapper">
        <div class="spreadsheet-container">
          <table class="spreadsheet-table" :style="{ width: totalTableWidth }">
            <thead class="table-header">
              <tr>
                <th 
                  v-for="(column, index) in visibleColumns" 
                  :key="column" 
                class="table-header-cell"
                :class="{ 
                  'dragging': draggedColumnIndex === index,
                  'drag-over': dragOverIndex === index
                }"
                :style="{ width: getColumnWidth(column) }"
                draggable="true"
                @dragstart="handleDragStart(index, $event)"
                @dragover.prevent="handleDragOver(index, $event)"
                @drop="handleDrop(index, $event)"
                @dragend="handleDragEnd"
              >
                <div class="header-content" @click.stop="handleColumnSort(column, $event)">
                  <span class="column-name-text">
                    {{ column }}
                    <span v-if="getSortColumn() === column" class="sort-indicator">
                      {{ getSortDirection() === 'asc' ? '↑' : '↓' }}
                    </span>
                  </span>
                  <div class="header-actions">
                    <button 
                      v-if="availableColumnsToAdd.length > 0"
                      @click.stop="showHeaderInsertMenu(index)" 
                      class="header-add-btn"
                      title="Add column to the right"
                    >
                      +
                    </button>
                    <button 
                      @click.stop="removeColumnFromHeader(index)" 
                      class="header-remove-btn"
                      title="Remove column"
                    >
                      ×
                    </button>
                  </div>
                </div>
                <div 
                  class="column-resize-handle"
                  @mousedown.stop="startResize(index, column, $event)"
                  @mouseenter="onResizeHandleHover($event)"
                  @mouseleave="onResizeHandleLeave($event)"
                  title="Drag to resize column"
                ></div>
                <div v-if="headerInsertMenuIndex === index" class="header-insert-menu">
                  <div class="column-search-container">
                    <input
                      type="text"
                      v-model="columnSearchQuery"
                      @keyup.stop
                      @click.stop
                      placeholder="Search columns..."
                      class="column-search-input"
                    />
                  </div>
                  <div class="available-columns-list">
                    <div 
                      v-for="col in filteredAvailableColumns"
                      :key="col"
                      @click.stop="insertColumnAtHeader(index, col)"
                      class="available-column-item"
                    >
                      {{ col }}
                    </div>
                    <div v-if="filteredAvailableColumns.length === 0" class="no-available-columns">
                      <span v-if="columnSearchQuery">No columns match "{{ columnSearchQuery }}"</span>
                      <span v-else>All columns are already added</span>
                    </div>
                  </div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in foodsArray" :key="item.id || index" class="table-row">
              <td 
                v-for="column in visibleColumns" 
                :key="column" 
                class="table-cell"
                :style="{ width: getColumnWidth(column) }"
              >
                <input
                  v-if="column !== 'id'"
                  type="text"
                  :value="getCellValue(item, column)"
                  @keyup="handleCellEdit(item, column, $event)"
                  @input="handleCellInput(item, column, $event)"
                  class="cell-input"
                  :class="{ 'cell-edited': isCellEdited(item.id, column) }"
                />
                <span
                  v-else
                  class="cell-readonly"
                  @click="openMenuView(item)"
                  @mouseenter="hoveredItemId = item.id"
                  @mouseleave="hoveredItemId = null"
                  :title="`Click to view menu for item ${item.id}`"
                >
                  {{ formatCellValue(item[column]) }}
                  <button
                    v-if="hoveredItemId === item.id"
                    @click.stop="duplicateItem(item)"
                    class="duplicate-btn"
                    :title="`Duplicate item ${item.id}`"
                  >
                    <img src="@/assets/duplicate.svg" alt="Duplicate" />
                  </button>
                </span>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div v-else class="no-data">
        <p>No foods data available</p>
      </div>
      <div v-if="hasUnsavedChanges" class="save-button-container">
        <button @click="saveAllChanges" class="save-btn" :disabled="saving">
          {{ saving ? 'Saving...' : 'Save Changes' }}
        </button>
      </div>
      </div>
    </template>
  </div>
</template>

<script>
import AdminMenuView from './AdminMenuView.vue'

export default {
  name: 'AdminFoods',
  components: {
    AdminMenuView
  },
  data() {
    return {
      foodsData: null,
      loading: true,
      error: null,
      searchQuery: '',
      selectedColumns: [],
      headerInsertMenuIndex: null,
      draggedColumnIndex: null,
      dragOverIndex: null,
      columnSearchQuery: '',
      columnWidths: {},
      resizingColumn: null,
      resizeStartX: 0,
      resizeStartWidth: 0,
      menuIds: [''],
      editedValues: {}, // Track edited values: { 'foodId_column': newValue }
      originalValues: {}, // Track original values: { 'foodId_column': originalValue }
      saving: false,
      showMenuView: false,
      selectedMenuId: null,
      selectedFoodId: null,
      hoveredItemId: null,
      duplicating: false,
      searchDebounceTimer: null,
      menuIdDebounceTimer: null
    }
  },
  computed: {
    isAdmin() {
      return this.$store.getters.getUserType === 'admin';
    },
    foodsArray() {
      // Handle different data structures
      if (!this.foodsData) return [];
      
      // If it's already an array, return it
      if (Array.isArray(this.foodsData)) {
        return this.foodsData;
      }
      
      // If it's an object with a foods/food/items key, use that
      if (this.foodsData.foods && Array.isArray(this.foodsData.foods)) {
        return this.foodsData.foods;
      }
      if (this.foodsData.food && Array.isArray(this.foodsData.food)) {
        return this.foodsData.food;
      }
      if (this.foodsData.items && Array.isArray(this.foodsData.items)) {
        return this.foodsData.items;
      }
      
      // If it's an object, convert to array
      if (typeof this.foodsData === 'object') {
        return [this.foodsData];
      }
      
      return [];
    },
    allAvailableColumns() {
      if (this.foodsArray.length === 0) return [];
      
      // Get all unique keys from all items
      const allKeys = new Set();
      this.foodsArray.forEach(item => {
        if (item && typeof item === 'object') {
          Object.keys(item).forEach(key => allKeys.add(key));
        }
      });
      
      return Array.from(allKeys).sort();
    },
    visibleColumns() {
      // Return only selected columns that exist in the data
      if (this.selectedColumns.length === 0) {
        return this.getDefaultColumns();
      }
      // Filter to only include columns that exist in the data
      return this.selectedColumns.filter(col => this.allAvailableColumns.includes(col));
    },
    availableColumnsToAdd() {
      // Return columns that are available but not yet selected
      return this.allAvailableColumns.filter(col => !this.selectedColumns.includes(col));
    },
    filteredAvailableColumns() {
      // Filter available columns based on search query
      if (!this.columnSearchQuery.trim()) {
        return this.availableColumnsToAdd;
      }
      const query = this.columnSearchQuery.toLowerCase().trim();
      return this.availableColumnsToAdd.filter(col => 
        col.toLowerCase().includes(query)
      );
    },
    totalTableWidth() {
      // Calculate total table width as sum of all column widths
      let total = 0;
      this.visibleColumns.forEach(column => {
        if (this.columnWidths[column]) {
          total += Math.max(50, this.columnWidths[column]);
        } else {
          // Default width for columns without explicit width
          total += 150;
        }
      });
      return total + 'px';
    },
    hasUnsavedChanges() {
      const hasChanges = Object.keys(this.editedValues).length > 0;
      // Debug logging (remove in production)
      if (hasChanges) {
        console.log('Unsaved changes detected:', Object.keys(this.editedValues).length, 'edits');
      }
      return hasChanges;
    }
  },
  created() {
    // Initialize search query from route
    this.searchQuery = this.$route.query.search || '';
    // Initialize menu IDs from route
    this.initializeMenuIds();
    // Load column preferences from localStorage
    this.loadColumnPreferences();
    // Load column widths from localStorage
    this.loadColumnWidths();
    // Check for session first, then verify admin status
    this.checkForSession();
  },
  mounted() {
    // Close insert menu when clicking outside
    document.addEventListener('click', this.handleClickOutside);
    // Add resize event listeners
    document.addEventListener('mousemove', this.handleResize);
    document.addEventListener('mouseup', this.stopResize);
  },
  beforeDestroy() {
    // Clear any pending debounce timers
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    if (this.menuIdDebounceTimer) {
      clearTimeout(this.menuIdDebounceTimer);
    }
    // Remove event listeners
    document.removeEventListener('click', this.handleClickOutside);
    document.removeEventListener('mousemove', this.handleResize);
    document.removeEventListener('mouseup', this.stopResize);
  },
  watch: {
    // Watch for route query changes to refetch data
    '$route.query': {
      handler(newQuery) {
        this.searchQuery = newQuery.search || '';
        // Update menu IDs if they changed in the route
        const menuIdParam = newQuery.menu_id;
        if (menuIdParam !== undefined) {
          const ids = String(menuIdParam).split(',').map(id => id.trim()).filter(id => id);
          if (ids.length > 0) {
            this.menuIds = ids;
          } else if (!menuIdParam) {
            this.menuIds = [''];
          }
        }
        if (this.isAdmin && !this.loading) {
          this.fetchFoods();
        }
      },
      deep: true
    }
  },
  methods: {
    getSortColumn() {
      return this.$route.query.sort_by || null;
    },
    getSortDirection() {
      return this.$route.query.sort_direction || null;
    },
    checkForSession() {
      if (localStorage.signedIn === "true") {
        this.$http.secured
          .get("/app_current_user")
          .then((response) => {
            this.$store.commit("setLoggedIn", response.data.user);
            this.$store.commit("setUserType", response.data.user_type);
            this.$store.commit("setUserEmail", response.data.email);
            this.$store.commit(
              "setUserCSVAccess",
              response.data.access_to_menu_csv_import_and_export
            );
            // Check admin status after updating store
            if (this.isAdmin) {
              this.fetchFoods();
            } else {
              // Not an admin, redirect to home
              this.$router.push('/');
            }
          })
          .catch((error) => {
            console.error('Error checking session:', error);
            // On error, redirect to home
            this.$router.push('/');
          });
      } else {
        // Not signed in, redirect to home
        this.$router.push('/');
      }
    },
    handleSearch() {
      // Clear existing timer
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      // Set new timer for 2 seconds
      this.searchDebounceTimer = setTimeout(() => {
        // Update the route query parameter with the search value
        this.updateRouteQuery();
      }, 2000);
    },
    initializeMenuIds() {
      const menuIdParam = this.$route.query.menu_id;
      if (menuIdParam) {
        // Split by comma and filter out empty strings
        const ids = String(menuIdParam).split(',').map(id => id.trim()).filter(id => id);
        this.menuIds = ids.length > 0 ? ids : [''];
      } else {
        this.menuIds = [''];
      }
    },
    handleMenuIdSearch() {
      // Clear existing timer
      if (this.menuIdDebounceTimer) {
        clearTimeout(this.menuIdDebounceTimer);
      }
      // Set new timer for 2 seconds
      this.menuIdDebounceTimer = setTimeout(() => {
        // Update route with menu_id query parameter
        this.updateRouteQuery();
      }, 2000);
    },
    addMenuId() {
      this.menuIds.push('');
    },
    removeMenuId(index) {
      if (this.menuIds.length > 1) {
        this.menuIds.splice(index, 1);
        this.updateRouteQuery();
      }
    },
    handleColumnSort(column, event) {
      // Don't sort if we're currently dragging or resizing
      if (this.draggedColumnIndex !== null || this.resizingColumn !== null) {
        return;
      }
      
      // Don't sort if clicking on buttons (they have @click.stop)
      if (event.target.closest('.header-actions') || event.target.closest('.column-resize-handle')) {
        return;
      }
      
      const currentSortColumn = this.getSortColumn();
      const currentSortDirection = this.getSortDirection();
      
      let newSortColumn = column;
      let newSortDirection = 'asc';
      
      if (currentSortColumn === column) {
        // Same column clicked - cycle through states
        if (currentSortDirection === 'asc') {
          newSortDirection = 'desc';
        } else if (currentSortDirection === 'desc') {
          // Third click - remove sorting
          newSortColumn = null;
          newSortDirection = null;
        }
      }
      
      // Update route with new sort parameters
      const query = { ...this.$route.query };
      
      if (newSortColumn) {
        query.sort_by = newSortColumn;
        query.sort_direction = newSortDirection;
      } else {
        delete query.sort_by;
        delete query.sort_direction;
      }
      
      this.$router.replace({ query: query });
    },
    updateRouteQuery() {
      const query = { ...this.$route.query };
      
      // Update search query
      if (this.searchQuery.trim()) {
        query.search = this.searchQuery.trim();
      } else {
        delete query.search;
      }
      
      // Update menu_id query - combine all non-empty menu IDs
      const validMenuIds = this.menuIds.map(id => id.trim()).filter(id => id);
      if (validMenuIds.length > 0) {
        query.menu_id = validMenuIds.join(',');
      } else {
        delete query.menu_id;
      }
      
      // Preserve sort parameters
      if (this.getSortColumn()) {
        query.sort_by = this.getSortColumn();
        query.sort_direction = this.getSortDirection();
      }
      
      // Update the route without triggering navigation
      this.$router.replace({ query: query });
    },
    getDefaultColumns() {
      return [
        'id',
        'menu_id',
        'sort',
        'category_header',
        'price',
        'unit',
        'translation_en',
        'description_en',
        'translation_zh',
        'description_zh',
        'translation_ru',
        'description_ru'
      ];
    },
    loadColumnPreferences() {
      const stored = localStorage.getItem('adminFoodsColumnPreferences');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed) && parsed.length > 0) {
            this.selectedColumns = parsed;
            return;
          }
        } catch (e) {
          console.error('Error parsing column preferences:', e);
        }
      }
      // Use default columns if nothing stored
      this.selectedColumns = this.getDefaultColumns();
      this.saveColumnPreferences();
    },
    saveColumnPreferences() {
      try {
        localStorage.setItem('adminFoodsColumnPreferences', JSON.stringify(this.selectedColumns));
      } catch (e) {
        console.error('Error saving column preferences:', e);
      }
    },
    resetColumnsToDefault() {
      // Reset to default columns
      this.selectedColumns = this.getDefaultColumns();
      this.saveColumnPreferences();
      
      // Remove sort parameters from URL
      const query = { ...this.$route.query };
      delete query.sort_by;
      delete query.sort_direction;
      this.$router.replace({ query: query });
    },
    handleClickOutside(event) {
      // Close header insert menu if clicking outside
      if (this.headerInsertMenuIndex !== null) {
        const table = this.$el.querySelector('.spreadsheet-table');
        if (table && !table.contains(event.target)) {
          this.headerInsertMenuIndex = null;
          this.columnSearchQuery = ''; // Reset search when closing
        }
      }
    },
    showHeaderInsertMenu(index) {
      // Only show menu if there are available columns to add
      if (this.availableColumnsToAdd.length === 0) {
        return;
      }
      // Toggle insert menu - close if clicking the same header, open if different
      if (this.headerInsertMenuIndex === index) {
        this.headerInsertMenuIndex = null;
        this.columnSearchQuery = ''; // Reset search when closing
      } else {
        this.headerInsertMenuIndex = index;
        this.columnSearchQuery = ''; // Reset search when opening
        // Focus the search input after menu opens
        this.$nextTick(() => {
          const menu = this.$el.querySelector('.header-insert-menu');
          if (menu) {
            const searchInput = menu.querySelector('.column-search-input');
            if (searchInput) {
              searchInput.focus();
            }
          }
        });
      }
    },
    insertColumnAtHeader(headerIndex, column) {
      // Insert column to the right of the clicked header (headerIndex + 1)
      const insertPosition = headerIndex + 1;
      this.selectedColumns.splice(insertPosition, 0, column);
      
      // Set a default width for the newly added column if it doesn't have one
      if (!this.columnWidths[column]) {
        this.$set(this.columnWidths, column, 150); // Default width of 150px
        this.saveColumnWidths();
      }
      
      this.saveColumnPreferences();
      this.headerInsertMenuIndex = null; // Close the menu after insertion
      this.columnSearchQuery = ''; // Reset search after insertion
    },
    removeColumnFromHeader(headerIndex) {
      // Remove the column at the specified header index
      // Find the actual column name from visibleColumns
      const columnToRemove = this.visibleColumns[headerIndex];
      if (columnToRemove) {
        const indexInSelected = this.selectedColumns.indexOf(columnToRemove);
        if (indexInSelected !== -1) {
          this.selectedColumns.splice(indexInSelected, 1);
          this.saveColumnPreferences();
        }
      }
      // Close insert menu if open
      this.headerInsertMenuIndex = null;
    },
    handleDragStart(index, event) {
      this.draggedColumnIndex = index;
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/html', index);
      // Add a slight delay to allow the drag image to be set
      setTimeout(() => {
        if (event.target) {
          event.target.style.opacity = '0.5';
        }
      }, 0);
    },
    handleDragOver(index, event) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      if (this.draggedColumnIndex !== null && this.draggedColumnIndex !== index) {
        this.dragOverIndex = index;
      }
    },
    handleDrop(dropIndex, event) {
      event.preventDefault();
      if (this.draggedColumnIndex === null || this.draggedColumnIndex === dropIndex) {
        this.dragOverIndex = null;
        return;
      }
      
      // Get the column names
      const draggedColumn = this.visibleColumns[this.draggedColumnIndex];
      const dropColumn = this.visibleColumns[dropIndex];
      
      if (draggedColumn && dropColumn) {
        // Find indices in selectedColumns array
        const draggedIndex = this.selectedColumns.indexOf(draggedColumn);
        const dropIndexInSelected = this.selectedColumns.indexOf(dropColumn);
        
        if (draggedIndex !== -1 && dropIndexInSelected !== -1) {
          // Remove the dragged column from its current position
          this.selectedColumns.splice(draggedIndex, 1);
          
          // Insert it at the new position
          const newIndex = draggedIndex < dropIndexInSelected ? dropIndexInSelected : dropIndexInSelected;
          this.selectedColumns.splice(newIndex, 0, draggedColumn);
          
          // Save preferences
          this.saveColumnPreferences();
        }
      }
      
      this.dragOverIndex = null;
    },
    handleDragEnd(event) {
      this.draggedColumnIndex = null;
      this.dragOverIndex = null;
      if (event.target) {
        event.target.style.opacity = '1';
      }
    },
    formatCellValue(value) {
      if (value === null || value === undefined) {
        return '';
      }
      if (typeof value === 'object') {
        return JSON.stringify(value);
      }
      return String(value);
    },
    fetchFoods() {
      this.loading = true;
      this.error = null;
      
      // Get query parameters from the route
      const queryParams = this.$route.query;
      
      this.$http.secured
        .get('/admin/foods', { params: queryParams })
        .then((response) => {
          this.foodsData = response.data;
          this.loading = false;
        })
        .catch((error) => {
          console.error('Error fetching foods:', error);
          this.error = error.response?.data?.message || error.message || 'Failed to fetch foods data';
          this.loading = false;
        });
    },
    getColumnWidth(column) {
      if (this.columnWidths[column]) {
        // Enforce minimum width of 50px
        const width = Math.max(50, this.columnWidths[column]);
        return width + 'px';
      }
      // Always return a pixel value, never 'auto'
      // Default width of 150px for columns without explicit width
      return '150px';
    },
    loadColumnWidths() {
      const stored = localStorage.getItem('adminFoodsColumnWidths');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (parsed && typeof parsed === 'object') {
            // Ensure all widths are at least 50px
            const validatedWidths = {};
            Object.keys(parsed).forEach(key => {
              validatedWidths[key] = Math.max(50, parsed[key]);
            });
            this.columnWidths = validatedWidths;
          }
        } catch (e) {
          console.error('Error parsing column widths:', e);
        }
      }
    },
    saveColumnWidths() {
      try {
        localStorage.setItem('adminFoodsColumnWidths', JSON.stringify(this.columnWidths));
      } catch (e) {
        console.error('Error saving column widths:', e);
      }
    },
    startResize(index, column, event) {
      event.preventDefault();
      this.resizingColumn = column;
      this.resizeStartX = event.clientX;
      
      // Get current width of the column, ensuring it's at least 50px
      const header = event.target.closest('th');
      if (header) {
        this.resizeStartWidth = Math.max(50, header.offsetWidth);
      } else {
        const storedWidth = this.columnWidths[column] || 150;
        this.resizeStartWidth = Math.max(50, storedWidth);
      }
      
      // Prevent text selection during resize
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    },
    handleResize(event) {
      if (!this.resizingColumn) return;
      
      const diff = event.clientX - this.resizeStartX;
      const newWidth = this.resizeStartWidth + diff;
      
      // Enforce minimum width of 50px
      const clampedWidth = Math.max(50, newWidth);
      this.columnWidths[this.resizingColumn] = clampedWidth;
      this.$forceUpdate(); // Force update to apply new width
    },
    stopResize() {
      if (this.resizingColumn) {
        this.saveColumnWidths();
        this.resizingColumn = null;
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      }
    },
    onResizeHandleHover(event) {
      const header = event.target.closest('th');
      if (header) {
        header.classList.add('resize-hover');
      }
    },
    onResizeHandleLeave(event) {
      const header = event.target.closest('th');
      if (header) {
        header.classList.remove('resize-hover');
      }
    },
    getCellValue(item, column) {
      const foodId = item.id;
      if (!foodId) return this.formatCellValue(item[column]);
      
      const key = `${foodId}:::${column}`;
      // Return edited value if exists, otherwise return original value
      if (this.editedValues.hasOwnProperty(key)) {
        return this.editedValues[key];
      }
      return this.formatCellValue(item[column]);
    },
    handleCellInput(item, column, event) {
      const foodId = item.id;
      if (!foodId) {
        console.warn('Food item missing id, cannot track edits:', item);
        return;
      }
      
      const key = `${foodId}:::${column}`;
      const newValue = event.target.value;
      
      // Store original value if this is the first edit
      if (!this.originalValues.hasOwnProperty(key)) {
        const originalFormatted = this.formatCellValue(item[column]);
        this.originalValues[key] = originalFormatted;
      }
      
      // Update edited value
      const originalValue = this.originalValues[key];
      
      // Normalize values for comparison (convert both to strings)
      const normalizedNew = String(newValue || '');
      const normalizedOriginal = String(originalValue || '');
      
      // If the value matches the original, remove from edited values
      if (normalizedNew === normalizedOriginal) {
        this.$delete(this.editedValues, key);
        this.$delete(this.originalValues, key);
      } else {
        this.$set(this.editedValues, key, newValue);
      }
    },
    handleCellEdit(item, column, event) {
      // This is triggered on keyup - save button visibility is handled by hasUnsavedChanges computed
      // No additional action needed here, but keeping for potential future use
    },
    isCellEdited(foodId, column) {
      if (!foodId) return false;
      const key = `${foodId}:::${column}`;
      return this.editedValues.hasOwnProperty(key);
    },
    openMenuView(item) {
      if (!item.menu_id) {
        console.error('Item missing menu_id:', item)
        return
      }
      this.selectedMenuId = item.menu_id
      this.selectedFoodId = item.id
      this.showMenuView = true
    },
    closeMenuView() {
      this.showMenuView = false
      this.selectedMenuId = null
      this.selectedFoodId = null
    },
    refreshFoods() {
      this.fetchFoods()
    },
    async duplicateItem(item) {
      if (this.duplicating || !item || !item.id) {
        console.error('Cannot duplicate item:', item)
        return
      }
      
      this.duplicating = true
      this.error = null
      
      try {
        // Make the API call to clone the item - only pass food ID in URL, no request body
        await this.$http.secured.post(`/app_clone_food/${item.id}`)
        
        // Refresh the foods data after successful clone
        await this.fetchFoods()
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Failed to duplicate item'
        console.error('Error duplicating item:', error)
      } finally {
        this.duplicating = false
      }
    },
    async saveAllChanges() {
      if (this.saving || !this.hasUnsavedChanges) return;
      
      this.saving = true;
      this.error = null;
      
      // Group edits by food item
      const editsByFood = {};
      Object.keys(this.editedValues).forEach(key => {
        const [foodId, ...columnParts] = key.split(':::');
        const column = columnParts.join(':::');
        if (!editsByFood[foodId]) {
          editsByFood[foodId] = {};
        }
        editsByFood[foodId][column] = this.editedValues[key];
      });
      
      // Save each food item
      const savePromises = [];
      for (const foodId in editsByFood) {
        const foodItem = this.foodsArray.find(item => item.id == foodId);
        if (!foodItem || !foodItem.menu_id) {
          console.error(`Food item ${foodId} not found or missing menu_id`);
          continue;
        }
        
        const updateData = editsByFood[foodId];
        const promise = this.$http.secured.post(
          `/app_update_food/${foodItem.menu_id}/${foodId}`,
          { food: updateData }
        ).then(response => {
          // Update the local data with the saved values
          Object.keys(updateData).forEach(column => {
            if (foodItem) {
              foodItem[column] = updateData[column];
            }
          });
          return response;
        }).catch(error => {
          console.error(`Error updating food ${foodId}:`, error);
          throw error;
        });
        
        savePromises.push(promise);
      }
      
      try {
        await Promise.all(savePromises);
        // Clear edited values after successful save
        this.editedValues = {};
        this.originalValues = {};
        // Optionally refetch data to ensure consistency
        // this.fetchFoods();
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Failed to save changes';
        this.$store.commit('setMessage', { content: "Changes failed to save", error: true })
        console.error('Error saving changes:', error);
      } finally {
        this.$store.commit('setMessage', { content: "Changes saved successfully" })
        this.saving = false;
      }
    }
  }
}
</script>

<style scoped>
.container {
  width: 90%;
  max-width: 1200px;
  margin: auto;
  margin-top: 100px;
  padding: 20px;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 30px;
}

h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
}

.refresh-btn {
  width: 24px;
  height: 24px;
  background-color: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;
}

.refresh-btn:hover {
  transform: rotate(180deg);
}

.refresh-btn img {
  width: 15px;
  height: 15px;
}

.controls-container {
  display: flex;
  gap: 15px;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.search-container {
  flex: 1;
  min-width: 200px;
}

.admin-label {
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 2.5px 0;
}

.search-input {
  width: 100%;
  max-width: 500px;
  padding: 12px 16px;
  /* font-size: 16px; */
  border: 1px solid #ddd;
  border-radius: 4px;
  outline: none;
  transition: border-color 0.2s;
}

.search-input:focus {
  border-color: #4a90e2;
  box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
}

.reset-columns-btn {
  padding: 12px 20px;
  font-size: 14px;
  border: 1px solid #ddd;
  background-color: #fff;
  color: #333;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.reset-columns-btn:hover {
  background-color: #f5f5f5;
  border-color: #999;
}

.reset-columns-btn:active {
  background-color: #e8e8e8;
}

.menu-id-container {
  margin-bottom: 20px;
}

.menu-id-inputs {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 200px;
}

.menu-id-input-wrapper {
  display: flex;
  gap: 8px;
  align-items: center;
}

.menu-id-input {
  flex: 1;
  padding: 12px 16px;
  /* font-size: 16px; */
  border: 1px solid #ddd;
  border-radius: 4px;
  outline: none;
  transition: border-color 0.2s;
  max-width: 300px;
}

.menu-id-input:focus {
  border-color: #4a90e2;
  box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
}

.remove-menu-id-btn {
  width: 15px;
  height: 15px;
  border: 1px solid #000;
  background-color: transparent;
  color: #000;
  border-radius: 5%;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 0;
  flex-shrink: 0;
}

.remove-menu-id-btn:hover {
  background-color: #000;
  color: #fff;
}

.add-menu-id-btn {
  width: 15px;
  height: 15px;
  border: 0px;
  /* border: 1px solid #000; */
  background-color: transparent;
  color: #000;
  border-radius: 5%;
  cursor: pointer;
  font-size: 15px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 0;
  flex-shrink: 0;
  align-self: flex-start;
}

.add-menu-id-btn:hover {
  background-color: #000;
  color: #fff;
}

.available-column-item {
  padding: 10px 15px;
  cursor: pointer;
  font-size: 14px;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
  white-space: nowrap;
}

.available-column-item:hover {
  background-color: #f5f5f5;
}

.available-column-item:last-child {
  border-bottom: none;
}

.no-available-columns {
  padding: 15px;
  text-align: center;
  color: #999;
  font-size: 13px;
}

.loader {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.lds-ellipsis {
  display: inline-block;
  position: relative;
  width: 80px;
  height: 80px;
}

.lds-ellipsis div {
  position: absolute;
  top: 33px;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  background: black;
  animation-timing-function: cubic-bezier(0, 1, 1, 0);
}

.lds-ellipsis div:nth-child(1) {
  left: 8px;
  animation: lds-ellipsis1 0.6s infinite;
}

.lds-ellipsis div:nth-child(2) {
  left: 8px;
  animation: lds-ellipsis2 0.6s infinite;
}

.lds-ellipsis div:nth-child(3) {
  left: 32px;
  animation: lds-ellipsis2 0.6s infinite;
}

.lds-ellipsis div:nth-child(4) {
  left: 56px;
  animation: lds-ellipsis3 0.6s infinite;
}

@keyframes lds-ellipsis1 {
  0% {
    transform: scale(0);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes lds-ellipsis3 {
  0% {
    transform: scale(1);
  }
  100% {
    transform: scale(0);
  }
}

@keyframes lds-ellipsis2 {
  0% {
    transform: translate(0, 0);
  }
  100% {
    transform: translate(24px, 0);
  }
}

.error-message {
  padding: 20px;
  background-color: #fee;
  border: 1px solid #fcc;
  border-radius: 4px;
  color: #c33;
}

.spreadsheet-wrapper {
  position: relative;
}

.spreadsheet-container {
  overflow-x: auto;
  overflow-y: visible;
  border: 0px;
  border-radius: 4px;
  position: relative;
}


.save-button-container {
  position: fixed;
  background: #39B54A;
  color: white;
  bottom: 0;
  width: 100%;
  box-sizing: border-box;
  left: 0;
  display: flex;
  flex-direction: row;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  padding: 20px;
  font-weight: 600;
  z-index: 1000;
}

.save-btn {
  width: 100%;
  padding: 0;
  font-size: inherit;
  font-weight: inherit;
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
  text-align: center;
  font-family: 'Montserrat', sans-serif;
}

.save-btn:hover:not(:disabled) {
  opacity: 0.9;
}

.save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spreadsheet-table {
  border-collapse: collapse;
  background-color: #fff;
  font-size: 14px;
  position: relative;
  table-layout: fixed;
}

.table-header {
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  background-color: rgba(251, 251, 251, 1);
  /* border-top: 1px solid black; */
}

.table-header-cell {
  /* background-color: #f5f5f5; */
  /* border: 1px solid #ddd; */
  border-bottom: 1px solid black;
  /* border-top: 1px solid black; */
  padding: 10px 12px;
  text-align: left;
  font-weight: 600;
  position: sticky;
  font-size: 12px;
  height: 25px;
  top: 0;
  z-index: 10;
  white-space: nowrap;
  position: relative;
  overflow: visible;
  min-width: 50px !important;
}

.table-header-cell.dragging {
  opacity: 0.5;
  cursor: grabbing;
}

.table-header-cell.drag-over {
  border-left: 3px solid #4a90e2;
  background-color: #e8f4fd;
}

.table-header-cell[draggable="true"] {
  cursor: grab;
}

.table-header-cell[draggable="true"]:active {
  cursor: grabbing;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  position: relative;
}

.column-name-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 4px;
}

.sort-indicator {
  font-size: 12px;
  color: #4a90e2;
  font-weight: bold;
}

.header-actions {
  display: none;
  align-items: center;
  gap: 8px;
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 15;
  background-color: rgba(245, 245, 245, 0.95);
  padding: 2px 4px;
  border-radius: 3px;
}

.table-header-cell:hover .header-actions {
  display: flex;
}

.header-add-btn {
  width: 15px;
  height: 15px;
  border: 1px solid #000;
  background-color: transparent;
  color: #000;
  border-radius: 5%;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 0;
  flex-shrink: 0;
}

/* .header-add-btn:hover {
  background-color: #000;
  color: #fff;
} */

.header-remove-btn {
  width: 15px;
  height: 15px;
  border: 1px solid #000;
  background-color: transparent;
  color: #000;
  border-radius: 5%;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 0;
  flex-shrink: 0;
}

/* .header-remove-btn:hover {
  background-color: #000;
  color: #fff;
} */

.column-resize-handle {
  position: absolute;
  top: 0;
  right: 0;
  width: 5px;
  height: 100%;
  cursor: col-resize;
  z-index: 25;
  background-color: transparent;
  transition: background-color 0.2s;
}

.column-resize-handle:hover {
  background-color: #4a90e2;
}

.table-header-cell.resize-hover {
  border-right: 2px solid #4a90e2;
}

.header-insert-menu {
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 250px;
  width: max-content;
  max-width: 400px;
  background-color: #fff;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 4px 4px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  max-height: 300px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: visible;
}

.column-search-container {
  padding: 8px;
  border-bottom: 1px solid #e0e0e0;
  background-color: #f9f9f9;
}

.column-search-input {
  width: 90%;
  padding: 8px 12px;
  font-size: 13px;
  border: 1px solid #ddd;
  border-radius: 4px;
  outline: none;
  transition: border-color 0.2s;
}

.column-search-input:focus {
  border-color: #4a90e2;
  box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.1);
}

.available-columns-list {
  max-height: 250px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

/* .table-row {
  border-bottom: 1px solid #e0e0e0;
} */

.table-row:hover {
  background-color: #f9f9f9;
}

.table-cell {
  border: 1px solid #e0e0e0;
  padding: 0;
  white-space: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  position: relative;
  min-width: 50px !important;
  /* Hide scrollbar for Chrome, Safari and Opera */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.table-cell::-webkit-scrollbar {
  display: none; /* Chrome, Safari and Opera */
}

.cell-input {
  width: 100%;
  min-width: 100%;
  border: none;
  padding: 10px 12px;
  font-size: 14px;
  background-color: transparent;
  outline: none;
  box-sizing: border-box;
}

.cell-input:focus {
  background-color: #f0f7ff;
  outline: 2px solid #4a90e2;
  outline-offset: -2px;
}

.cell-input.cell-edited {
  background-color: #fff9e6;
  /* outline: 2px solid #4a90e2;
  outline-offset: -2px; */
}

.cell-input.cell-edited:focus {
  background-color: #fff4cc;
}

.cell-readonly {
  display: block;
  padding: 10px 35px 10px 12px;
  font-size: 14px;
  color: black;
  user-select: none;
  font-family: Arial, sans-serif;
  cursor: pointer;
  transition: background-color 0.2s;
  position: relative;
}

.cell-readonly:hover {
  background-color: #f0f7ff;
}

.duplicate-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  background-color: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.duplicate-btn img {
  width: 16px;
  height: 16px;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.duplicate-btn:hover img {
  opacity: 1;
}

.duplicate-btn:active {
  transform: translateY(-50%) scale(0.95);
}

.no-data {
  padding: 40px;
  text-align: center;
  color: #666;
}
</style>

